import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Paper, CircularProgress, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import GetAppIcon from '@mui/icons-material/GetApp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useClienteApi } from '../../../infra/cliente/useClienteApi';
import { useAxiosInstances } from '../../../infra/axios-instance';
import { useUIService } from '../../../use-cases/ui';
import { useAppSelector } from '../../hooks/store-hooks';
import { ITipoUsuario } from '../../../application/models/ITipoUsuario';

interface ContratoTabProps {
  IdCliente: number;
  isEditMode: boolean;
}

export const ContratoTab: React.FC<ContratoTabProps> = ({ IdCliente, isEditMode }) => {
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [fetchingPdf, setFetchingPdf] = useState(false);
  const { uploadContratoApi } = useClienteApi();
  const { setAlert } = useUIService();
  const { axiosAuthInstance } = useAxiosInstances();
  const { currentUser } = useAppSelector((state: any) => state.user);

  const isVelesDemais = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_DEMAIS
  const isTecnologiaDaInformacao = currentUser?.IdVelesPerfilInterno === ITipoUsuario.TECNOLOGIA_DA_INFORMACAO;
  const isCliente = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_CLIENTE;

  const loadContratoPdf = async () => {
    if (!IdCliente || IdCliente === 0) return;
    setFetchingPdf(true);
    try {
      const response = await axiosAuthInstance.get(`/cliente/${IdCliente}/contrato`, {
        responseType: 'blob'
      });
      
      if (response.status === 204) {
        setPdfUrl(null);
        return;
      }

      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      setPdfUrl(fileURL);
    } catch (err) {
      // Se não encontrou ou deu erro, apenas limpa a URL do PDF (cliente não tem contrato ainda)
      setPdfUrl(null);
    } finally {
      setFetchingPdf(false);
    }
  };

  useEffect(() => {
    loadContratoPdf();
    // Limpeza da URL do objeto para evitar memory leaks
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [IdCliente]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      
      if (file.type !== 'application/pdf') {
        setAlert({ message: 'Apenas arquivos PDF são permitidos.', type: 'error' });
        event.target.value = '';
        return;
      }

      if (!IdCliente || IdCliente === 0) {
        setAlert({ message: 'Cliente não salvo ainda. Salve o cliente primeiro.', type: 'error' });
        return;
      }
      
      setLoading(true);
      try {
        await uploadContratoApi({ IdCliente, file });
        setAlert({ message: 'Contrato salvo com sucesso', type: 'success' });
        
        // Recarrega o PDF recém salvo para exibir no iframe
        await loadContratoPdf();
      } catch (err) {
        setAlert({ message: 'Erro ao fazer upload do contrato', type: 'error' });
      } finally {
        setLoading(false);
      }
      
      // Reseta o input para permitir selecionar o mesmo arquivo novamente se necessário
      event.target.value = '';
    }
  };

  return (
    <Box p={2}>
      <Typography variant="h6" color="primary" gutterBottom>
        Contrato do Cliente
      </Typography>
      
      {isEditMode && (isVelesDemais || isTecnologiaDaInformacao) ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', mb: 2 }}>
          <input
            accept="application/pdf"
            style={{ display: 'none' }}
            id="raised-button-file"
            type="file"
            onChange={handleFileChange}
            disabled={loading || fetchingPdf}
          />
          <label htmlFor="raised-button-file">
            <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />} disabled={loading || fetchingPdf}>
              {loading ? <CircularProgress size={24} color="inherit" /> : (pdfUrl ? 'Substituir PDF' : 'Fazer Upload do PDF')}
            </Button>
          </label>
        </Paper>
      ) : isEditMode && !(isVelesDemais || isTecnologiaDaInformacao) ? (
        <Box display="flex" flexDirection="column" gap={1} mb={2}>
           <Typography variant="body2" color="warning.main">
            Somente usuários do perfil <strong>Demais</strong> podem alterar o arquivo de contrato.
          </Typography>
          {pdfUrl && isCliente && (
            <Button 
              variant="contained" 
              startIcon={<GetAppIcon />} 
              href={pdfUrl} 
              download={`Contrato_Cliente_${IdCliente}.pdf`}
              sx={{ width: 'fit-content' }}
            >
              Baixar Contrato Atual
            </Button>
          )}
        </Box>
      ) : (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="body1">
            Modo visualização {isVelesDemais && '(Gestor)'}
          </Typography>
          {pdfUrl && (isVelesDemais || isTecnologiaDaInformacao || isCliente) && (
            <Button 
              variant="outlined" 
              startIcon={<GetAppIcon />} 
              href={pdfUrl} 
              download={`Contrato_Cliente_${IdCliente}.pdf`}
            >
              Baixar PDF
            </Button>
          )}
        </Box>
      )}

      {fetchingPdf ? (
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>Carregando contrato...</Typography>
        </Box>
      ) : pdfUrl ? (
        <Accordion variant="outlined" defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">Visualizar Documento</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Box sx={{ width: '100%', height: '800px', overflow: 'hidden' }}>
              <iframe 
                src={`${pdfUrl}${(isVelesDemais || isTecnologiaDaInformacao) ? '' : '#toolbar=0&navpanes=0'}`} 
                width="100%" 
                height="100%" 
                title="Preview do Contrato"
                style={{ border: 'none', display: 'block' }}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      ) : (
        isEditMode && (
          <Typography variant="body2" color="textSecondary" align="center">
            Nenhum contrato salvo para este cliente. Faça o upload acima.
          </Typography>
        )
      )}
    </Box>
  );
};
